import { useParams } from 'react-router-dom';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { LegalLayout } from '../components/LegalLayout';
import { isConvenienceTranslation, resolveLegalLang, type LegalDocKey } from '../legalDocs';
import { useLegalContent } from '../useLegalContent';

interface Props {
  doc: LegalDocKey;
}

/**
 * Renders one corporate legal document in the active language. Content is
 * fetched from /legal/{lang}.json (self-contained — not the booking i18n).
 * Non-English translations carry an "English version governs" convenience
 * notice; the authoritative ES Privacy/T&C do not (see isConvenienceTranslation).
 */
export const LegalDocPage = ({ doc }: Props) => {
  const { lang } = useParams<{ lang?: string }>();
  const activeLang = resolveLegalLang(lang);
  const { data, isError } = useLegalContent(activeLang);

  const docContent = data?.[doc];
  const showConvenienceNotice = isConvenienceTranslation(doc, activeLang);

  return (
    <LegalLayout doc={doc} activeLang={activeLang}>
      {docContent ? (
        <article>
          <h1
            className="mb-8 font-display text-3xl text-foreground sm:text-4xl"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(docContent.title) }}
          />

          {showConvenienceNotice && data?.convenience_notice && (
            <p className="mb-8 border-l-2 border-brand pl-4 text-sm italic text-muted-foreground">
              {data.convenience_notice}
            </p>
          )}

          <div
            className="text-[15px] leading-relaxed text-foreground/80 [&_a]:text-brand [&_a]:underline [&_a]:underline-offset-2 [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:font-display [&_h2]:text-xl [&_h2]:text-foreground [&_li]:pl-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:space-y-1.5 [&_ol]:pl-6 [&_p]:mb-4 [&_strong]:font-semibold [&_strong]:text-foreground [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-6"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(docContent.body) }}
          />
        </article>
      ) : isError ? (
        <p className="text-muted-foreground">
          This document is temporarily unavailable. Please try again later.
        </p>
      ) : (
        <div className="space-y-3" aria-hidden="true">
          <div className="h-9 w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-48 animate-pulse rounded bg-muted" />
        </div>
      )}
    </LegalLayout>
  );
};

export default LegalDocPage;
